/**
 * 时长档合身度(2026-09-13 A1/A6)—— 「你写的这些,装进这个时长档合不合身」的**只提示**信号。
 *
 * ═══ 治的是什么 ═══════════════════════════════════════════════════════════════
 * `durationAdvice`(shotBudget.ts)只说「这个档装得下多少」(容量),不看用户**实际写了多少**。
 * 老板要的更进一步:选了「超长 60s」却只写了一句话 → 成片会拖/反复凑时长;写了一大段却选
 * 「短 15s」→ 念不完被裁、画面赶。这两种错配此前用户只能等成片出来才发现。
 *
 * ═══ 判据(粗档,不精算)═══════════════════════════════════════════════════════
 * 「用户信息量」infoScore = 文案字数 + 素材数 × MATERIAL_WEIGHT
 *   —— 口播/卖点主要来自文案;产品图/人物给一个内容缓冲(有图 = 有内容,别误报「信息少」)。
 * 「这个档的口播字上限」charBudget = voiceoverCharBudget(targetSec)(= 秒数 × 5.85,超了硬裁)。
 *   contentBeats(引擎节拍表的内容位数,来自 taxonomy 的 beatPlan)给了就随消息一起说清「能讲几个点」。
 *
 * ★三态**只有两种会警告**(与老板口径一致,不为凑齐第三态编话):
 *   - sparse:信息少 + 长档(≥45s)→ 会拖/重复,建议改短一档 + 去补内容;
 *   - dense :信息多 + 短档(≤15s)→ 会念糊/被裁,建议改长一档;
 *   - fit   :其余(含 30s 甜点档)→ 不打扰。
 * ★只提示、**不禁用主 CTA**(消费方 A6:不拦截)。分档阈值天生粗糙,宁可漏报不误伤。
 */
import type { VideoTaxonomyBeatPlanDto } from "../api/videoService";
import { briefTargetSec } from "./effectiveDuration";
import { voiceoverCharBudget } from "./shotBudget";
import type { FormState } from "./videoCreateTypes";

export type DurationFitState = "fit" | "sparse" | "dense";

export interface DurationFit {
  state: DurationFitState;
  targetSec: number;
  /** 这个档的口播字上限(超了硬裁)。 */
  charBudget: number;
  /** 引擎节拍表的内容位数(taxonomy 给了才有);消息里说清「能讲几个点」用。 */
  contentBeats?: number;
  /** 一键改档的目标(sparse→更短一档 / dense→更长一档);两端没有更合适的档 → null。 */
  suggestTier: string | null;
}

/** 一个素材(产品图/人物)约折算多少字的「内容分量」—— 只为避免「有图却报信息少」。 */
const MATERIAL_WEIGHT = 40;
/** infoScore < charBudget × 这个比例,且是长档 → 信息偏少。 */
const SPARSE_RATIO = 0.45;
/** infoScore > charBudget × 这个比例,且是短档 → 信息偏多。 */
const DENSE_RATIO = 1.6;

/** 上架时长档(秒 → 中文档名,一键改档按钮用中文,与创建页 chip 同口径)。 */
const TIER_BY_SEC: readonly { sec: number; label: string }[] = [
  { sec: 15, label: "短" },
  { sec: 30, label: "中" },
  { sec: 45, label: "长" },
  { sec: 60, label: "超长" },
];

/** 相邻档:step=-1 更短、+1 更长;越界返 null(没有更合适的档就不给一键改档)。 */
function neighborTier(targetSec: number, step: -1 | 1): string | null {
  const i = TIER_BY_SEC.findIndex((t) => t.sec === targetSec);
  if (i < 0) return null;
  const next = TIER_BY_SEC[i + step];
  return next ? next.label : null;
}

/** 用户带进来的素材数(产品图实体 + 人物 + 旧版单产品便捷位)。 */
export function materialCount(form: Pick<FormState, "entities" | "characterId" | "productRefKey">): number {
  const products = (form.entities ?? []).filter(
    (e) => e.source === "image" && e.role === "product" && !!e.key
  ).length;
  const hasChar = form.characterId != null ? 1 : 0;
  // 旧草稿只有便捷位、没有 entities:也算一份产品料(不重复计:有 entities 产品就不再叠)。
  const legacyProduct = products === 0 && form.productRefKey ? 1 : 0;
  return products + hasChar + legacyProduct;
}

/**
 * @param beatPlan 当前目的 × 当前时长档的节拍摘要(taxonomy 的 `beatPlans[targetSec]`);
 *   未就绪 / 未下发时传 null,消息退到只按字数说(不假装有内容位数)。
 */
export function durationFit(
  form: Pick<FormState, "userInput" | "briefDuration" | "entities" | "characterId" | "productRefKey">,
  beatPlan?: VideoTaxonomyBeatPlanDto | null
): DurationFit | null {
  const targetSec = briefTargetSec(form.briefDuration);
  if (!targetSec) return null; // 没选时长档 → 不对着空档编合身度

  const charBudget = voiceoverCharBudget(targetSec);
  const contentBeats = beatPlan?.contentBeats && beatPlan.contentBeats > 0 ? beatPlan.contentBeats : undefined;
  const infoScore = (form.userInput ?? "").trim().length + materialCount(form) * MATERIAL_WEIGHT;

  // 信息少 + 长档(≥45s):会拖/重复 —— 建议改短一档 + 去补内容。
  if (targetSec >= 45 && infoScore < charBudget * SPARSE_RATIO) {
    return { state: "sparse", targetSec, charBudget, contentBeats, suggestTier: neighborTier(targetSec, -1) };
  }
  // 信息多 + 短档(≤15s):念不完被裁 / 画面赶 —— 建议改长一档。
  if (targetSec <= 15 && infoScore > charBudget * DENSE_RATIO) {
    return { state: "dense", targetSec, charBudget, contentBeats, suggestTier: neighborTier(targetSec, 1) };
  }
  return { state: "fit", targetSec, charBudget, contentBeats, suggestTier: null };
}
